import React, { useMemo } from "react";
import { Link, useParams, useSearchParams, Navigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Seo from "@/components/Seo";
import AuditCta from "@/components/AuditCta";
import { prepareArticleHtml } from "@/lib/articleHtml";
import { toJsonLd } from "@/lib/jsonld";
import useBlogArticle from "@/hooks/useBlogArticle";
import { BlogPostLoading, BlogPostNotFound } from "@/components/blog/BlogPostStates";

const SITE_URL = "https://www.rakisolutions.com";

const formatDate = (value) =>
  new Date(value).toLocaleDateString("en-SG", { year: "numeric", month: "long", day: "numeric" });

function ArticleSchema({ article, slug }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: article.title,
    description: article.meta_description || "",
    url: `${SITE_URL}/insights/${slug}`,
    datePublished: article.created_at,
    dateModified: article.updated_at || article.created_at,
    author: { "@type": "Organization", name: "Raki Solutions", url: SITE_URL },
    publisher: { "@type": "Organization", name: "Raki Solutions", url: SITE_URL },
    mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_URL}/insights/${slug}` },
  };
  if (article.hero_image_url) schema.image = article.hero_image_url;

  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: toJsonLd(schema) }} />;
}

const PROSE = `prose prose-lg max-w-none
  prose-headings:font-fraunces prose-headings:font-extrabold prose-headings:text-[#1F1F1C]
  prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
  prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
  prose-p:text-[#4A463F] prose-p:leading-relaxed
  prose-a:text-[#C8703A] prose-a:no-underline hover:prose-a:underline
  prose-strong:text-[#1F1F1C]
  prose-li:text-[#4A463F]
  prose-img:rounded-2xl prose-img:my-8
  prose-blockquote:border-[#C8703A] prose-blockquote:text-[#4A463F]`;

export default function InsightPostPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const { article, loading, error, gone } = useBlogArticle(searchParams.get("id"), slug);

  const articleHtml = useMemo(
    () =>
      article
        ? prepareArticleHtml(article.content_html, {
            title: article.title,
            heroImageUrl: article.hero_image_url,
          })
        : "",
    [article]
  );

  if (loading) return <BlogPostLoading />;
  if (gone) return <Navigate to="/insights" replace />;
  if (error || !article) return <BlogPostNotFound />;

  return (
    <main className="px-6 sm:px-8 pt-14 pb-20 sm:pb-28" data-testid="blog-post-page">
      <Seo
        title={article.title}
        description={article.meta_description || "Notes on sales automation and AI workflows from Raki Solutions in Singapore."}
        path={`/insights/${slug}`}
      />

      <div className="max-w-3xl mx-auto">
        <Link
          to="/insights"
          className="inline-flex items-center gap-1.5 text-sm text-[#4A463F] hover:text-[#C8703A] transition-colors"
          data-testid="blog-post-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Insights
        </Link>

        <header className="mt-10">
          <p className="text-xs uppercase tracking-[0.16em] font-semibold text-[#4A463F]">
            {formatDate(article.created_at)}
          </p>
          <h1 className="mt-4 font-fraunces font-extrabold text-4xl sm:text-5xl leading-[1.08] tracking-tight text-[#1F1F1C]" data-testid="blog-post-title">
            {article.title}
          </h1>
          {article.meta_description && (
            <p className="mt-6 text-base md:text-lg text-[#4A463F] leading-relaxed">{article.meta_description}</p>
          )}
        </header>

        {article.hero_image_url && (
          <img
            src={article.hero_image_url}
            alt={article.title}
            className="mt-10 w-full rounded-2xl object-cover max-h-[440px]"
            data-testid="blog-post-hero-image"
          />
        )}

        <article className={`mt-10 ${PROSE}`} data-testid="blog-post-content" dangerouslySetInnerHTML={{ __html: articleHtml }} />

        <div className="mt-16 bg-[#EADCC6] rounded-2xl border border-[#1F1F1C]/10 p-8 sm:p-10">
          <h2 className="font-fraunces font-extrabold text-2xl sm:text-3xl tracking-tight text-[#1F1F1C]">
            Want this running in your business?
          </h2>
          <p className="mt-4 text-sm md:text-base text-[#4A463F] leading-relaxed">
            30 minutes, free, and a one-page summary within 48 hours.
          </p>
          <div className="mt-7">
            <AuditCta testId="blog-post-cta" />
          </div>
        </div>

        <ArticleSchema article={article} slug={slug} />
      </div>
    </main>
  );
}
